import {
  Component,
  Input,
  OnDestroy,
  OnInit,
  SimpleChanges,
  inject,
  viewChild
} from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { finalize, takeUntil, tap } from 'rxjs/operators';

import {
  CreateOccasionScheduledMessageCommand,
  CustomFieldTypes,
  OrganizationCustomFieldsDropDownDto,
  SendTypes,
  UpdateOccasionScheduledMessageCommand,
  UseTypes,
} from 'src/app/core/api/models';
import { OccasionScheduledMessagesService } from 'src/app/core/api/services';
import { EventTypes } from 'src/app/core/constants/event-types';
import { EnumSendOccasionType } from 'src/app/core/enums/send-occasion-type.enum';
import { AppStateService } from 'src/app/core/services/app-state.service';
import { AppDomService } from 'src/app/core/services/dom.service';
import { TabsDynamicComponent } from 'src/app/layout/components/tabs-dynamic/tabs-dynamic.component';
import { GetOccasionScheduledMessageByIdPhonebookDto } from '../../../core/api/models';
import { DynamicTabService } from '../../../core/services/dynamic-tab.service';
import { UtilityService } from '../../../core/services/utility.service';
import { AppCalendarEvent } from '../../facility/calendar-event/app-calendar-event.type';
import { CalendarModalComponent } from '../../facility/calendar-event/calendar-modal/calendar-modal.component';
import { PhonebookSelectorComponent } from '../../facility/phonebook/phonebook-selector/phonebook-selector.component';
import { PhonebookCustomDto } from '../../facility/phonebook/shared/phonebook-custom.model';
import { PhonebookStateService } from '../../facility/phonebook/shared/phonebook-state.service';
import { BaseFormPageComponent } from '../../shared/base/components/base-form-page.component';
import { PrivateNumbersService } from './../../../core/api/services/private-numbers.service';
import { DialogService } from './../../../core/services/dialog.service';
import { BaseSendSmsConfig } from './../base/base-send-sms/base-send-sms.component';

@Component({
    selector: 'app-send-occasion-sms',
    templateUrl: './send-occasion-sms.component.html',
    styleUrls: ['../base/base-sending-system.component.scss'],
    standalone: false
})
export class SendOccasionSmsComponent
  extends BaseFormPageComponent
  implements OnInit, OnDestroy
{
  appStateService = inject(AppStateService);
  phonebookStateService = inject(PhonebookStateService);
  readonly phonebookSelectorComponent = viewChild(PhonebookSelectorComponent);
  //#region properties

  formId: string = 'SendOccasionSms';
  tabName: string = 'sendOccasionSms';
  model: CreateOccasionScheduledMessageCommand = {};
  calenderEventTitle: string = '';
  calenderOrganizationEventTitle: string = '';
  baseSendSmsConfig: BaseSendSmsConfig;
  selectedValue: number;
  selectedValueId: number;
  isOpenModal: boolean = true;
  occasionId: number;
  calenderOccasionTitle: string = '';
  refreshPhonebook: number = 0;
  smsCount: number;
  @Input() selectedPhonebooks: GetOccasionScheduledMessageByIdPhonebookDto[] =
    [];
  phonebookRecipients: PhonebookCustomDto[] = [];

  sendLoading: boolean;
  get SendTypes() {
    return SendTypes;
  }
  get UseTypes() {
    return UseTypes;
  }
  get CustomFieldTypes() {
    return CustomFieldTypes;
  }

  get SendOccasionType() {
    return EnumSendOccasionType;
  }

  occasionalPostingTypes: { id?: number; title?: string }[] = [];

  //#endregion

  constructor(
    protected currentService: OccasionScheduledMessagesService,
    public dialogService: DialogService,
    public utilityService: UtilityService,
    protected translate: TranslateService,
    public dynamicTabService: DynamicTabService,
    public privateNumberService: PrivateNumbersService,
    private tabsDynamicComponent: TabsDynamicComponent
  ) {
    super();
    this.baseCreateGetApiForSend(
      this.currentService,
      'occasionScheduledMessagesCreateGet'
    );
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes?.selectedPhonebooks && this.selectedPhonebooks?.length) {
      this.phonebookRecipients = this.selectedPhonebooks.map((p) => ({
        checked: true,
        isValid: true,
        id: p.phonebookId,
        name: p.phonebookName,
        contactsCount: p.phonebookContactsCount,
        fromIndex: 1,
        count: p.phonebookContactsCount,
      }));
    }
  }

  ngAfterViewInit(): void {
    //edit mode
    const phonebookSelectorComponent = this.phonebookSelectorComponent();
    if (phonebookSelectorComponent && this.phonebookRecipients?.length) {
      phonebookSelectorComponent.phonebookRecipients =
        this.phonebookRecipients;
      phonebookSelectorComponent.applyPhonebookRecipients();
    }
  }

  ngOnInit() {
    try {
      this.model.phonebookIdList = [];

      super.ngOnInit();

      this.calenderEventTitle =
        this.formPageConfig.editData?.calenderEventTitle;
      this.calenderOrganizationEventTitle =
        this.formPageConfig.editData?.calenderOrganizationEventTitle;

      this.pageVocabs = 'pages.sendOccasionSms.';
      this.fields = 'pages.sendOccasionSms.fields.';

      this.occasionalPostingTypes = [
        {
          id: EnumSendOccasionType.CalendarEvent,
          title: this.translate.instant(this.fields + 'calendarEvents'),
        },
        {
          id: EnumSendOccasionType.CalenderOrganizationEvent,
          title: this.translate.instant(
            this.fields + 'calenderOrganizationEvents'
          ),
        },
        {
          id: EnumSendOccasionType.CustomField,
          title: this.translate.instant('common.customField'),
        },
      ];
      this.typeValueInEditMode();
      this.configBaseSendSMS();
      this.form.valueChanges
        .pipe(this.basePipeOperators())
        .subscribe((Changes) => {
          this.changeFormStatus();
        });
    } catch (error) {
      this.loggerService.logError(
        this.tagName,
        error,
        EventTypes.sendingSystem
      );
    }
  }

  closeThisTab() {
    try {
      const TimeOut = setTimeout(() => {
        this.tabsDynamicComponent.manualCloseTab(this.tabName);
        clearTimeout(TimeOut);
      }, 50);
    } catch (error) {
      this.loggerService.logError(
        this.tagName,
        error,
        EventTypes.sendingSystem
      );
    }
  }

  //#region init

  configBaseSendSMS() {
    try {
      this.baseSendSmsConfig = {
        title: this.pageVocabs + 'title',
        isEdit: this.formPageConfig.isEditMode,
        isShowPageHeader: true,
        isAddSignature: !this.formPageConfig?.isEditMode ? true : false,
        isAddHeader: !this.formPageConfig?.isEditMode ? true : false,
        isShowCampaign: true,
        isShowExtraReceivers: true,
        msgInfoClassList: 'sm:tw-w-1/4 tw-mt-2',
        receiversClassList: 'sm:tw-w-5/12 tw-mt-2 overflow-auto',
        sendDateTimeClassList: 'sm:tw-w-1/3 tw-mt-2',
        isShowReceivers: true,
        isShowFastSend: false,

        sendType: SendTypes.OccasionPhonebook,
        useType: UseTypes.Normal,
      };
    } catch (error) {
      this.loggerService.logError(
        this.tagName,
        error,
        EventTypes.sendingSystem
      );
    }
  }

  initForm() {
    this.form = new FormGroup({
      sendTime: new FormControl('', Validators.required),
      senderNumberId: new FormControl('', Validators.required),
      dynamicTemplateId: new FormControl(''),
      howManyDaysSendItBeforeDueDate: new FormControl(''),
      isActive: new FormControl(''),
      toConsiderYear: new FormControl(''),
      type: new FormControl('', Validators.required),
      smsBody: new FormControl(
        ''
        // Validators.pattern(RegularExpressions.smsBodyForbiddenCharacters)
      ),
      customField: new FormControl(''),
      calenderOrganizationOccasion: new FormControl(''),
      calenderOccasion: new FormControl(''),
    });
  }

  changeFormStatus() {
    this.form.setErrors(null);
  }

  //#endregion

  //#region Api

  //create
  baseCreateApi(model: CreateOccasionScheduledMessageCommand) {
    this.unSubs();

    this.sendLoading = true;
    this.currentService
      .occasionScheduledMessagesCreatePost({ body: model })
      .pipe(
        this.basePipeOperators(),
        finalize(() => (this.sendLoading = false))
      )
      .subscribe((result) => {
        this.apiNotificationService.notifyMessage({
          result,
          mode: 'matDialog',
        });
        if (result.isSucceed) {
          this.onSuccess();
          this.refreshPage();
        }
      });
  }

  baseUpdateApi(model: UpdateOccasionScheduledMessageCommand) {
    this.unSubs();
    this.loading = true;

    this.currentService
      .occasionScheduledMessagesUpdatePut({ body: model })
      .pipe(this.basePipeOperators())
      .subscribe(this.baseObserver);
  }

  //#endregion

  //#region set values

  changePhonebookIds(phonebookIds: any[]) {
    this.model.phonebookIdList = phonebookIds?.map(
      ({ phonebookId }) => phonebookId
    );
  }

  setCustomField(event: OrganizationCustomFieldsDropDownDto) {
    this.model.phonebookCustomFieldNameEn = event.nameEn;
  }

  onRefreshPhonebook() {
    this.refreshPhonebook++;
  }

  //#endregion

  //#region events
  onShowEventsModal(eventType: AppCalendarEvent, event) {
    event.srcElement?.classList.forEach((className) => {
      if (className === 'mat-icon') this.isOpenModal = false;
    });

    if (this.isOpenModal) {
      const modalResult = this.dialogService.openModal({
        component: CalendarModalComponent,
        data: eventType,
      });
      modalResult
        .afterClosed()
        .pipe(
          takeUntil(this.onDestroy$),
          tap(() => {
            AppDomService.removeModalScrollClassFromHtml();
          })
        )
        .subscribe((res) => {
          if (res) {
            if (res.eventType == 'calendarEvent') {
              this.model.calenderEventId = res.eventId;
              this.calenderEventTitle = res.eventTitle;
            } else if (res.eventType == 'calendarOrganizationEvent') {
              this.model.calenderOrganizationEventId = res?.eventId;
              this.calenderOrganizationEventTitle = res?.eventTitle;
            }
          }
        });
    }
    this.isOpenModal = true;
  }
  //#endregion

  onSubmit() {
    try {
      if (
        !this.phonebookStateService.checkForEmptyRecipients(
          this.model.phonebookIdList,
          'emptyReceivers'
        )
      )
        return;

      if (!this.isValidForm(this.form, this.tagName)) return;
      if (this.loading) return;
      this.checkCreateOrUpdate();
    } catch (error) {
      this.loggerService.logError(
        this.tagName,
        error,
        EventTypes.sendingSystem
      );
    }
  }

  checkCreateOrUpdate() {
    if (this.loading || !this.form.valid) return;

    if (this.formPageConfig.isEditMode)
      this.baseUpdateApi({
        calenderEventId: this.model.calenderEventId,
        calenderOrganizationEventId: this.model.calenderOrganizationEventId,
        dynamicTemplateId: this.model.dynamicTemplateId,
        howManyDaysSendItBeforeDueDate:
          this.model.howManyDaysSendItBeforeDueDate,
        id: this.formPageConfig.editId,
        isActive: this.model.isActive,
        phonebookCustomFieldNameEn: this.model.phonebookCustomFieldNameEn,
        phonebookIdList: this.model.phonebookIdList,
        privateNumberId: this.model.privateNumberId,
        sendDateTime: this.model.sendDateTime,
        smsText: this.model.smsText,
        toConsiderYear: this.model.toConsiderYear,
      });
    else this.baseCreateApi(this.model);
  }

  //#region page
  refreshPage() {
    this.tabsDynamicComponent.refreshTab(this.tabName);
  }

  openPage(tabName: string) {
    try {
      this.dynamicTabService.setNewTab('');
      this.dynamicTabService.setNewTab(tabName);
      this.dynamicTabService.setActiveTab(tabName);
    } catch (error) {
      this.loggerService.logError(
        this.tagName,
        error,
        EventTypes.sendingSystem
      );
    }
  }
  //#endregion

  // clear occasion fields value
  occasionInputClear(type: string) {
    if (type == 'typeSelectSearch') {
      this.selectedValue = 0;
      this.calenderOrganizationEventTitle = '';
      this.calenderEventTitle = '';
    } else {
      this.calenderOrganizationEventTitle = '';
      this.calenderEventTitle = '';
    }
  }

  // change input validators when option in select search Change
  occasionType(id: number) {
    this.selectedValueId = id;

    // set Calendar Event Validators
    if (this.selectedValueId == EnumSendOccasionType.CalendarEvent) {
      if (this.model?.calenderOrganizationEventId)
        this.model.calenderOrganizationEventId = null;

      this.form.get('calenderOccasion').setValidators(Validators.required);
      this.form.get('calenderOrganizationOccasion').clearValidators();
      this.form.get('customField').clearValidators();
    }

    // set Calender Organization Event Validators
    if (
      this.selectedValueId == EnumSendOccasionType.CalenderOrganizationEvent
    ) {
      if (this.model?.calenderEventId) this.model.calenderEventId = null;

      this.form
        .get('calenderOrganizationOccasion')
        .setValidators(Validators.required);
      this.form.get('calenderOccasion').clearValidators();
      this.form.get('customField').clearValidators();
    }

    // set custom Field
    if (this.selectedValueId == EnumSendOccasionType.CustomField) {
      if (this.model?.calenderOrganizationEventId)
        this.model.calenderOrganizationEventId = null;

      if (this.model?.calenderEventId) this.model.calenderEventId = null;

      this.form.get('customField').setValidators(Validators.required);
      this.form.get('calenderOrganizationOccasion').clearValidators();
      this.form.get('calenderOccasion').clearValidators();
    }

    this.model.phonebookCustomFieldNameEn = '';
    this.calenderOccasionTitle = '';
  }

  typeValueInEditMode() {
    if (this.model?.calenderEventId) {
      this.selectedValue = EnumSendOccasionType.CalendarEvent;
    }

    if (this.model?.calenderOrganizationEventId) {
      this.selectedValue = EnumSendOccasionType.CalenderOrganizationEvent;
    }

    if (this.model?.phonebookCustomFieldNameEn) {
      this.selectedValue = EnumSendOccasionType.CustomField;
    }
  }

  // onShowHelpPage() {
  //   this.isVisibleHelpPage = true;
  // }

  onHideHelpPage() {
    this.pageConfig.isVisibleHelpPage = false;
  }
}
